import { useState } from 'react'
import {
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { FS_MICRO, FS_XS, ICON_XL, FS_MD, ICON_SM, ICON_XS, FS_SM, ICON_MD, ICON_XXS, FS_LG, FS_XL } from '../common/theme'
import { useApp, useAppActions, useNav } from '../AppContext'
import { buildCoreUrl } from '../common/common'
import cfpLogo from '../img/cfplogo2.svg'
import cfpLogoOnly from '../img/cfplogoonly.svg'
import { TABS, LITE_TABS } from '../pages/AppPage'

const EXPANDED_WIDTH = 180
const COLLAPSED_WIDTH = 50


export default function SideNav() {
  const { activeTab, setActiveTab } = useNav()
  const { health, acInit, liteMode, taskCount, runningTaskCount, queuedTaskCount } = useApp()
  const { setLiteMode } = useAppActions()
  const accountHref = buildCoreUrl('/account')
  const overQuota = acInit?.quota?.overquota ?? false

  const [collapsed, setCollapsed] = useState(false)
  const navWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH


  return (
    <Box
      id="fullsidenav"
      data-testid="sn_panel"
      data-collapsed={collapsed ? 'true' : 'false'}
      sx={{
        width: navWidth,
        flex: `0 0 ${navWidth}px`,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        transition: 'width 0.2s ease, flex-basis 0.2s ease',
        userSelect: 'none',
      }}
    >
      {/* Logo */}
      <Box
        component="a"
        href={accountHref}
        data-testid="sn_logo_link"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          textDecoration: 'none',
          py: 1,
          px: 1
        }}
      >
        <img
          src={collapsed ? cfpLogoOnly : cfpLogo}
          alt="Contract Finder Pro"
          style={collapsed ? { width: 32, height: 32 } : { maxWidth: '100%', height: 'auto' }}
        />
      </Box>

      <Divider />

      {/* Tab list */}
      <List disablePadding data-testid="sn_tablist" sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {TABS.map(tab => (
          <Box
            key={tab.id}
            sx={tab.grouped && !collapsed ? { borderLeft: 2, borderColor: 'divider', ml: '3px' } : undefined}
          >
            {tab.groupStart && collapsed && <Divider sx={{ my: 0.5 }} />}
            {tab.groupStart && !collapsed && (
              <Typography
                variant="caption"
                sx={{ display: 'block', px: 1.5, pt: 1, pb: 0.25, opacity: 0.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: FS_MICRO }}
              >
                {tab.groupStart}
              </Typography>
            )}
            <Tooltip
              title={liteMode && !LITE_TABS.has(tab.id) ? `${tab.label} not available in Lite Mode` : tab.label}
              placement="right"
              arrow
              disableHoverListener={!collapsed && !liteMode}
            >
              <span style={{ display: 'block' }}>
                <ListItemButton
                  data-testid="sn_tab"
                  data-tab-id={tab.id}
                  data-tab-active={activeTab === tab.id ? 'true' : 'false'}
                  selected={activeTab === tab.id}
                  disabled={liteMode && !LITE_TABS.has(tab.id)}
                  onClick={() => setActiveTab(tab.id)}
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    py: 0.75,
                    px: collapsed ? 0 : 1,
                    pl: (!collapsed && tab.grouped) ? 2.5 : (collapsed ? 0 : 1),
                    gap: collapsed ? 0 : 1,
                    position: 'relative',
                    minHeight: 44,
                    borderLeft: !collapsed && tab.grouped ? 0 : 3,
                    borderColor: activeTab === tab.id ? 'primary.main' : 'transparent',
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      fontWeight: 600,
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: 'action.selected',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 'unset', justifyContent: 'center', flexShrink: 0 }}>
                    <img src={tab.icon} alt={tab.label} style={{ width: 24, height: 24 }} />
                  </ListItemIcon>

                  {!collapsed && (
                    <ListItemText
                      primary={tab.label}
                      sx={{ m: 0 }}
                      primaryTypographyProps={{ fontSize: FS_MD, lineHeight: 1.3 }}
                    />
                  )}

                  {tab.id === 'tasks' && taskCount > 0 && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.25,
                      ml: collapsed ? 0 : 'auto',
                      position: collapsed ? 'absolute' : 'static',
                      top: collapsed ? 4 : undefined,
                      right: collapsed ? 4 : undefined,
                      flexDirection: collapsed ? 'column' : 'row',
                    }}>
                      {runningTaskCount > 0 && (
                        <Chip
                          size="small"
                          label={runningTaskCount}
                          color="primary"
                          sx={{
                            height: FS_LG, fontSize: FS_SM, fontWeight: 600,
                            '& .MuiChip-label': { px: '5px' }
                          }}
                        />
                      )}
                      {queuedTaskCount > 0 && (
                        <Chip
                          size="small"
                          icon={<AccessTimeIcon />}
                          label={queuedTaskCount}
                          sx={{
                            height: FS_XL, fontSize: FS_SM, fontWeight: 600,
                            bgcolor: 'action.disabledBackground', color: 'text.secondary',
                            '& .MuiChip-icon': { fontSize: ICON_XXS, ml: '4px', mr: '-2px', mb: '1px', color: 'text.secondary' },
                            '& .MuiChip-label': { px: '5px', mt: '1px' }
                          }}
                        />
                      )}
                    </Box>
                  )}
                  {tab.id === 'settings' && overQuota && (
                    <WarningAmberIcon
                      color="warning"
                      sx={{ fontSize: ICON_XL, position: 'absolute', top: 4, right: 4 }}
                    />
                  )}
                </ListItemButton>
              </span>
            </Tooltip>
          </Box>
        ))}
      </List>

      {/* Bottom group */}
      <Box sx={{ flexShrink: 0 }}>

        {/* Lite Mode toggle */}
        <Divider />
        <Tooltip title="Lite Mode" placement="right" disableHoverListener={!collapsed}>
          <Box
            data-testid="sn_litemode_toggle"
            data-litemode={liteMode ? 'true' : 'false'}
            onClick={() => {
              const next = !liteMode
              setLiteMode(next)
              if (next && !LITE_TABS.has(activeTab)) setActiveTab('knowledge')
            }}
            sx={{
              display: 'flex', alignItems: 'center', px: 1, py: 0.5,
              justifyContent: collapsed ? 'center' : 'space-between',
              cursor: 'pointer', userSelect: 'none',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {!collapsed && (
              <Typography variant="caption" sx={{ opacity: liteMode ? 1 : 0.65, fontSize: FS_XS }}>Lite Mode</Typography>
            )}
            <Switch size="small" checked={liteMode} onChange={() => { }} sx={{ m: 0, pointerEvents: 'none' }}
              inputProps={{ 'data-testid': 'sn_litemode_switch' } as any} />
          </Box>
        </Tooltip>

        <Divider />

        {/* Collapse toggle */}
        <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', px: 0.5, py: 0.5 }}>
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <IconButton
              size="small"
              data-testid="sn_collapse_btn"
              onClick={() => setCollapsed(v => !v)}
              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
            >
              {collapsed
                ? <ChevronRightIcon sx={{ fontSize: ICON_XL }} />
                : <ChevronLeftIcon sx={{ fontSize: ICON_XL }} />
              }
            </IconButton>
          </Tooltip>
        </Box>

        <Divider />

        {/* Help Centre */}
        <Tooltip title="Help Centre" placement="right" disableHoverListener={!collapsed}>
          <Box
            component="a"
            data-testid="sn_help_link"
            href="./docs/index.html"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 0.5,
              p: 1,
              textDecoration: 'none',
              color: 'inherit',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: ICON_SM, flexShrink: 0 }} />
            {!collapsed && (
              <>
                <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden' }}>
                  Help Centre
                </Typography>
                <OpenInNewIcon sx={{ fontSize: ICON_XS, flexShrink: 0 }} />
              </>
            )}
          </Box>
        </Tooltip>

        {/* Connection status */}
        <Tooltip
          title={health.success ? 'Connected' : (health.msg || 'Disconnected')}
          placement="right"
          disableHoverListener={!collapsed && health.success}
        >
          <Box
            data-testid="sn_connection_status"
            data-connected={health.success ? 'true' : 'false'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 0.5,
              px: 1,
              pb: 1,
            }}
          >
            <FiberManualRecordIcon
              sx={{ fontSize: 10, color: health.success ? 'success.main' : 'error.main', flexShrink: 0 }}
            />
            {!collapsed && (
              <Typography variant="caption">
                {health.success ? 'Connected' : 'Disconnected'}
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  )
}
